/** 

=========================================================
* Vision UI PRO React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Visionware.

*/

import React, { useMemo, useEffect, useState } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";

// Vision UI Dashboard PRO React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiSelect from "components/VuiSelect";
import VuiInput from "components/VuiInput";
import VuiPagination from "components/VuiPagination";

// Vision UI Dashboard PRO React example components
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";

function DataTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
  onPageChange,
  onEntriesPerPageChange,
}) {
  const defaultValue = entriesPerPage.defaultValue ? entriesPerPage.defaultValue : 10;
  const entries = entriesPerPage.entries ? entriesPerPage.entries : [5, 10, 15, 20, 25];
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  // Custom global filter function that can search through React components
  const customGlobalFilter = useMemo(() => {
    return (rows, columnIds, filterValue) => {
      if (!filterValue) return rows;
      
      const searchValue = filterValue.toLowerCase();
      
      return rows.filter(row => {
        return Object.values(row.original).some(cellValue => {
          if (typeof cellValue === 'string') {
            return cellValue.toLowerCase().includes(searchValue);
          } else if (React.isValidElement(cellValue)) {
            // Extract text content from React elements
            const extractText = (element) => {
              if (typeof element === 'string') return element;
              if (typeof element === 'number') return element.toString();
              if (React.isValidElement(element)) {
                if (element.props && element.props.children) {
                  if (typeof element.props.children === 'string') {
                    return element.props.children;
                  } else if (Array.isArray(element.props.children)) {
                    return element.props.children.map(extractText).join(' ');
                  } else if (React.isValidElement(element.props.children)) {
                    return extractText(element.props.children);
                  }
                }
              }
              return '';
            };
            
            const textContent = extractText(cellValue);
            return textContent.toLowerCase().includes(searchValue);
          }
          return false;
        });
      });
    };
  }, []);

  const tableInstance = useTable(
    { 
      columns, 
      data, 
      initialState: { 
        pageIndex: onPageChange && pagination?.currentPage ? pagination.currentPage - 1 : 0 
      },
      globalFilter: customGlobalFilter,
      manualPagination: !!onPageChange, // Use manual pagination when custom handler is provided
      pageCount: onPageChange && pagination?.totalPages ? pagination.totalPages : undefined,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
  } = tableInstance;

  // Set the default value for the entries per page when component mounts
  useEffect(() => {
    if (onPageChange) {
      // For server-side pagination, don't set page size as it's controlled by server
      return;
    }
    setPageSize(defaultValue || 10);
  }, [defaultValue, onPageChange]);

  // Set the entries per page value based on the select value
  const setEntriesPerPage = ({ value }) => {
    if (onEntriesPerPageChange) {
      onEntriesPerPageChange(value);
    } else {
      setPageSize(value);
    }
  };

  // Render the paginations
  const renderPagination = onPageChange && pagination?.totalPages ? (
    // Server-side pagination: show up to 5 page numbers around current page
    Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
      const currentPage = pagination.currentPage || 1;
      const totalPages = pagination.totalPages || 1;
      const startPage = Math.max(1, currentPage - 2);
      const pageNum = startPage + i;
      
      if (pageNum > totalPages) return null;
      
      return (
        <VuiPagination
          item
          key={pageNum}
          onClick={() => onPageChange(pageNum)}
          active={currentPage === pageNum}
        >
          {pageNum}
        </VuiPagination>
      );
    }).filter(Boolean)
  ) : (
    // Client-side pagination: show all page options
    pageOptions.map((option) => (
      <VuiPagination
        item
        key={option}
        onClick={() => gotoPage(Number(option))}
        active={pageIndex === option}
      >
        {option + 1}
      </VuiPagination>
    ))
  );

  // Handler for the input to set the pagination index
  const handleInputPagination = ({ target: { value } }) => {
    const pageNum = Number(value);
    const maxPage = onPageChange && pagination?.totalPages ? pagination.totalPages : pageOptions.length;
    
    if (pageNum > maxPage || pageNum < 1) {
      // Invalid page number, don't change
      return;
    }
    
    if (onPageChange) {
      onPageChange(pageNum);
    } else {
      gotoPage(pageNum - 1); // Convert to 0-based index
    }
  };

  // Customized page options starting from 1
  const customizedPageOptions = pageOptions.map((option) => option + 1);

  // Setting value for the pagination input
  const handleInputPaginationValue = ({ target: value }) => gotoPage(Number(value.value - 1));

  // Search input value state
  const [search, setSearch] = useState(globalFilter);

  // Search input state handle
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  // A function that sets the sorted value for the table
  const setSortedValue = (column) => {
    let sortedValue;

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  // Setting the entries starting point
  const entriesStart = onPageChange && pagination?.currentPage 
    ? ((pagination.currentPage - 1) * pageSize) + 1
    : (pageIndex === 0 ? pageIndex + 1 : pageIndex * pageSize + 1);

  // Setting the entries ending point
  let entriesEnd;

  if (onPageChange && pagination?.currentPage) {
    // Server-side pagination
    const start = entriesStart;
    const totalItems = pagination.totalItems || rows.length;
    entriesEnd = Math.min(start + rows.length - 1, totalItems);
  } else {
    // Client-side pagination
    if (pageIndex === 0) {
      entriesEnd = pageSize;
    } else if (pageIndex === pageOptions.length - 1) {
      entriesEnd = rows.length;
    } else {
      entriesEnd = pageSize * (pageIndex + 1);
    }
  }

  return (
    <TableContainer sx={{ 
      boxShadow: "none", 
      overflow: "visible",
      width: "100%",
      maxWidth: "100%",
    }}>
      {canSearch ? (
        <VuiBox
          display="flex"
          p={3}
          pl={0}
          justifyContent="flex-end"
        >
          {canSearch && (
            <VuiBox width="12rem">
              <VuiInput
                placeholder="Search..."
                value={search}
                onChange={({ currentTarget }) => {
                  setSearch(search);
                  onSearchChange(currentTarget.value);
                }}
                sx={({ borders: { borderWidth }, palette: { inputColors } }) => ({
                  border: `${borderWidth[1]} solid`,
                  borderColor: inputColors.borderColor.main,
                })}
              />
            </VuiBox>
          )}
        </VuiBox>
      ) : null}
      <Table 
        {...getTableProps()}
        sx={{
          width: "100%",
          tableLayout: "fixed",
          minWidth: "800px",
        }}
      >
        <VuiBox component="thead">
          {headerGroups.map((headerGroup) => (
            <TableRow {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <DataTableHeadCell
                  {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                  width={column.width ? column.width : "auto"}
                  align={column.align ? column.align : "left"}
                  sorted={setSortedValue(column)}
                  sx={{
                    maxWidth: column.id === 'priorityReason' ? '300px' : 
                             column.id === 'platform' ? '150px' :
                             column.id === 'majorInfringement' ? '120px' :
                             column.id === 'priority' ? '80px' :
                             column.id === 'complianceStatus' ? '120px' :
                             column.id === 'uncompliedTakedowns' ? '120px' :
                             column.id === 'jurisdiction' ? '100px' :
                             column.id === 'alert' ? '80px' :
                             column.id === 'dateLastActivity' ? '120px' : 'auto',
                    wordWrap: 'break-word',
                    whiteSpace: 'normal',
                  }}
                >
                  {column.render("Header")}
                </DataTableHeadCell>
              ))}
            </TableRow>
          ))}
        </VuiBox>
        <TableBody {...getTableBodyProps()}>
          {(onPageChange ? rows : page).map((row, key) => {
            prepareRow(row);
            return (
              <TableRow {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <DataTableBodyCell
                    noBorder={noEndBorder && rows.length - 1 === key}
                    align={cell.column.align ? cell.column.align : "left"}
                    column={cell.column}
                    {...cell.getCellProps()}
                  >
                    {cell.render("Cell")}
                  </DataTableBodyCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <VuiBox
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        p={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}
        sx={{ overflow: "visible" }}
      >
        {showTotalEntries && (
          <VuiBox mb={{ xs: 3, md: 0 }}>
            <VuiTypography variant="button" color="white" fontWeight="regular">
              Showing {entriesStart} to {entriesEnd} of {onPageChange && pagination?.totalItems ? pagination.totalItems.toLocaleString() : rows.length} entries
            </VuiTypography>
          </VuiBox>
        )}
        <VuiBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
          {pageOptions.length > 1 && (
            <VuiPagination
              variant={pagination.variant ? pagination.variant : "gradient"}
              color={pagination.color ? pagination.color : "info"}
            >
              {(onPageChange ? (pagination?.currentPage || 1) > 1 : canPreviousPage) && (
                <VuiPagination item onClick={() => {
                  if (onPageChange) {
                    onPageChange((pagination?.currentPage || 1) - 1);
                  } else {
                    previousPage();
                  }
                }}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                </VuiPagination>
              )}
              {onPageChange ? (
                // Server-side pagination: show page buttons + input field
                <>
                  {renderPagination}
                  <VuiBox width="5rem" mx={1}>
                    <VuiInput
                      inputProps={{ 
                        type: "number", 
                        min: 1, 
                        max: pagination?.totalPages || 1
                      }}
                      value={pagination?.currentPage || 1}
                      onChange={handleInputPagination}
                    />
                  </VuiBox>
                </>
              ) : renderPagination.length > 6 ? (
                // Client-side pagination: show input field only if many pages
                <VuiBox width="5rem" mx={1}>
                  <VuiInput
                    inputProps={{ 
                      type: "number", 
                      min: 1, 
                      max: customizedPageOptions.length 
                    }}
                    value={customizedPageOptions[pageIndex]}
                    onChange={handleInputPagination}
                  />
                </VuiBox>
              ) : (
                renderPagination
              )}
              {(onPageChange ? (pagination?.currentPage || 1) < (pagination?.totalPages || 1) : canNextPage) && (
                <VuiPagination item onClick={() => {
                  if (onPageChange) {
                    onPageChange((pagination?.currentPage || 1) + 1);
                  } else {
                    nextPage();
                  }
                }}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                </VuiPagination>
              )}
            </VuiPagination>
          )}
          {entriesPerPage && (
            <VuiBox display="flex" alignItems="center" sx={{ position: 'relative', zIndex: 10 }}>
              <VuiSelect
                defaultValue={{ value: defaultValue, label: defaultValue }}
                options={entries.map((entry) => ({ value: entry, label: entry }))}
                onChange={setEntriesPerPage}
                size="small"
              />
              <VuiTypography variant="caption" color="white" ml={1}>
                entries per page
              </VuiTypography>
            </VuiBox>
          )}
        </VuiBox>
      </VuiBox>
    </TableContainer>
  );
}

// Setting default values for the props of DataTable
DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
};

// Typechecking props for the DataTable
DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  onPageChange: PropTypes.func,
  onEntriesPerPageChange: PropTypes.func,
};

export default DataTable;
